import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { TOPIC_ICONS, FALLBACK_TOPIC_ICON } from '@/features/topics/icons';
import { CompactQuoteRow } from './CompactQuoteRow';
import { EmptyShelf } from './EmptyShelf';
import type { ShelfItem } from '@/api/types';

function TopicIcon({ id }: { id: string | null }) {
  const Cmp = (id && TOPIC_ICONS[id]) || FALLBACK_TOPIC_ICON;
  return <Cmp size={18} />;
}

interface Props {
  items: ShelfItem[];
  onHide: (cardId: string, hide: boolean) => void;
  onDelete: (cardId: string) => void;
}

interface BookGroup {
  title: string | null;
  author: string | null;
  topic_id: string | null;
  items: ShelfItem[];
}

export function ShelfBooks({ items, onHide, onDelete }: Props) {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const map = new Map<string, BookGroup>();
    for (const item of items) {
      const g = map.get(item.book_id);
      if (!g) {
        map.set(item.book_id, { title: item.book_title, author: item.book_author, topic_id: item.topic_id ?? null, items: [item] });
      } else {
        if (!g.title && item.book_title) g.title = item.book_title;
        if (!g.author && item.book_author) g.author = item.book_author;
        if (!g.topic_id && item.topic_id) g.topic_id = item.topic_id;
        g.items.push(item);
      }
    }
    return map;
  }, [items]);

  const bookIds = Array.from(groups.keys());

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpanded(prev => {
      if (prev.size > 0) return prev; // user has already interacted — don't override
      const first = groups.keys().next().value;
      return first ? new Set([first]) : prev;
    });
  }, [groups]);

  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  if (bookIds.length === 0) {
    return <EmptyShelf title={t('shelf.empty')} />;
  }

  return (
    <div className="space-y-3">
      {Array.from(groups.entries()).map(([bookId, group]) => {
        const isOpen = expanded.has(bookId);
        return (
          <div key={bookId} className="rounded-xl bg-surface overflow-hidden">
            <button
              onClick={() => toggle(bookId)}
              className="w-full flex items-start gap-3 px-4 py-3 text-left"
            >
              <div className="w-8 h-8 rounded-md bg-accent/15 text-accent grid place-items-center shrink-0 mt-0.5">
                <TopicIcon id={group.topic_id} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text leading-snug break-words">{group.title ?? t('shelf.no_title')}</div>
                {group.author && (
                  <div className="text-xs text-text-muted mt-0.5 break-words">{group.author}</div>
                )}
                <div className="text-xs text-text-muted mt-1">{t('shelf.quotes_count', { count: group.items.length })}</div>
              </div>
              {isOpen
                ? <IconChevronDown size={16} className="text-text-muted shrink-0 mt-1" />
                : <IconChevronRight size={16} className="text-text-muted shrink-0 mt-1" />
              }
            </button>
            {isOpen && group.items.map(item => (
              <CompactQuoteRow key={item.id} item={item} onHide={onHide} onDelete={onDelete} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
